import { useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Save, Trash2 } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { NodeLibrary } from './NodeLibrary';
import { NodePropertyPanel } from './NodePropertyPanel';
import { nodeTypes } from './nodes';
import { DialplanNodeData } from '@/types/dialplan-nodes';

interface TenantFlowCanvasProps {
  initialNodes: Node[];
  initialEdges: Edge[];
  onSave: (nodes: Node[], edges: Edge[]) => Promise<void>;
  contextName: string;
}

export default function TenantFlowCanvas({
  initialNodes,
  initialEdges,
  onSave,
  contextName
}: TenantFlowCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Update nodes/edges when props change
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => addEdge(params, eds));
      setHasChanges(true);
    },
    [setEdges]
  );

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const position = {
        x: event.clientX - 250,
        y: event.clientY - 100,
      };

      const newNode: Node = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: {
          label: `New ${type}`,
          config: {},
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setHasChanges(true);
    },
    [setNodes]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(nodes, edges);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNode = useCallback(() => {
    if (!selectedNode) return;

    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
    setHasChanges(true);
  }, [selectedNode, setNodes, setEdges]);

  const handleUpdateNode = useCallback(
    (nodeId: string, data: Partial<DialplanNodeData>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n))
      );
      setHasChanges(true);
    },
    [setNodes]
  );

  return (
    <div className="flex h-full">
      {/* Node Library - Left Sidebar */}
      <div className="w-64 border-r bg-background overflow-y-auto">
        <NodeLibrary onNodeDragStart={() => {}} />
      </div>

      {/* Flow Canvas - Center */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          fitView
        >
          <Background />
          <Controls />
          <MiniMap />
          
          <Panel position="top-right" className="bg-background border rounded-lg p-2 shadow-lg">
            <div className="flex gap-2">
              {selectedNode && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleDeleteNode}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Node
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                <Save className="h-4 w-4 mr-1" />
                {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
              </Button>
            </div>
          </Panel>

          <Panel position="top-left" className="bg-background border rounded-lg p-2 shadow-lg">
            <div className="text-sm">
              <div className="font-semibold">Context: {contextName}</div>
              <div className="text-muted-foreground text-xs mt-1">
                {nodes.length} nodes, {edges.length} connections
              </div>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Node Property Panel - Right Sidebar */}
      {selectedNode && (
        <div className="w-80 border-l bg-background overflow-y-auto">
          <NodePropertyPanel
            selectedNode={selectedNode}
            onUpdate={handleUpdateNode}
            onClose={() => setSelectedNode(null)}
          />
        </div>
      )}
    </div>
  );
}

